/**
 * Single source of truth for all supported languages in the platform.
 *
 * EVERY place in the codebase that needs to know about supported languages
 * should import from here — no hardcoded arrays, regexes, or type literals.
 *
 * Adding a new language = adding one entry to SUPPORTED_LANGUAGES below.
 * Everything else (routing, ISR, sitemap, SEO, meta tags) adapts automatically.
 */

export interface LanguageDef {
  /** Short ISO 639-1 code used in URLs: 'pl', 'en', 'de', 'lt' */
  code: string;
  /** Full BCP 47 locale used by Angular i18n: 'pl-PL', 'en-GB' */
  locale: string;
  /** Hub URL keyword for this market: 'imprezy', 'events', 'renginiai' */
  hubKeyword: string;
  /** Ticket slug in event URLs: 'bilety', 'tickets', 'bilietai' */
  ticketKeyword: string;
  /** hreflang attribute value (usually same as code) */
  hreflang: string;
  /** Warmup priority: 0 = primary market (first), higher = later */
  priority: number;
}

/**
 * All active languages on the platform — ordered by priority.
 * Add new languages here; all consumers adapt automatically.
 *
 * Priority tiers:
 *   0 = primary market (Poland)
 *   1 = international fallback (English)
 *   2 = active expansion markets (DE, LT — organizers already onboarded)
 *   3 = EU expansion — major dance markets
 *   4 = EU expansion — smaller/emerging markets
 */
export const SUPPORTED_LANGUAGES: LanguageDef[] = [
  // --- Tier 0-2: Active markets ---
  { code: 'pl', locale: 'pl-PL', hubKeyword: 'imprezy',     ticketKeyword: 'bilety',     hreflang: 'pl', priority: 0 },
  { code: 'en', locale: 'en-GB', hubKeyword: 'events',      ticketKeyword: 'tickets',    hreflang: 'en', priority: 1 },
  { code: 'de', locale: 'de-DE', hubKeyword: 'events',      ticketKeyword: 'tickets',    hreflang: 'de', priority: 2 },

  // --- Tier 3: Major EU dance markets ---
  { code: 'fr', locale: 'fr-FR', hubKeyword: 'evenements',  ticketKeyword: 'billets',    hreflang: 'fr', priority: 3 },
  { code: 'es', locale: 'es-ES', hubKeyword: 'eventos',     ticketKeyword: 'entradas',   hreflang: 'es', priority: 3 },

  // --- TEMPORARILY DISABLED (April 2026 — reduce cache footprint & speed up warmup) ---
  // Uncomment individual lines to re-enable a language. Translation JSONs,
  // hub/ticket keyword mappings, and SEO configs are intact in the repo
  // under apps/**/i18n/ and @dibsondance/shared-title — just re-adding the
  // entry here is enough for the router + warmup + ISR to pick it up.
  //
  // Unknown language prefixes hitting the server (e.g. legacy Google
  // results pointing at /cs/…) are redirected to /en/ by
  // I18nRedirectMiddleware so SEO equity isn't lost.

  // --- Tier 2-3: Active / planned expansion ---
  // { code: 'lt', locale: 'lt-LT', hubKeyword: 'renginiai',   ticketKeyword: 'bilietai',   hreflang: 'lt', priority: 2 },
  // { code: 'it', locale: 'it-IT', hubKeyword: 'eventi',      ticketKeyword: 'biglietti',  hreflang: 'it', priority: 3 },
  // { code: 'pt', locale: 'pt-PT', hubKeyword: 'eventos',     ticketKeyword: 'bilhetes',   hreflang: 'pt', priority: 3 },
  // { code: 'nl', locale: 'nl-NL', hubKeyword: 'evenementen', ticketKeyword: 'tickets',    hreflang: 'nl', priority: 3 },

  // --- Tier 4: CEE & Nordic expansion ---
  // { code: 'cs', locale: 'cs-CZ', hubKeyword: 'udalosti',    ticketKeyword: 'vstupenky',  hreflang: 'cs', priority: 4 },
  // { code: 'sk', locale: 'sk-SK', hubKeyword: 'podujatia',   ticketKeyword: 'vstupenky',  hreflang: 'sk', priority: 4 },
  // { code: 'hu', locale: 'hu-HU', hubKeyword: 'esemenyek',   ticketKeyword: 'jegyek',     hreflang: 'hu', priority: 4 },
  // { code: 'ro', locale: 'ro-RO', hubKeyword: 'evenimente',  ticketKeyword: 'bilete',     hreflang: 'ro', priority: 4 },
  // { code: 'bg', locale: 'bg-BG', hubKeyword: 'sabitiya',    ticketKeyword: 'bileti',     hreflang: 'bg', priority: 4 },
  // { code: 'hr', locale: 'hr-HR', hubKeyword: 'dogadanja',   ticketKeyword: 'ulaznice',   hreflang: 'hr', priority: 4 },
  // { code: 'sl', locale: 'sl-SI', hubKeyword: 'dogodki',     ticketKeyword: 'vstopnice',  hreflang: 'sl', priority: 4 },
  // { code: 'el', locale: 'el-GR', hubKeyword: 'ekdiloseis',  ticketKeyword: 'eisitiria',  hreflang: 'el', priority: 4 },
  // { code: 'et', locale: 'et-EE', hubKeyword: 'sundmused',   ticketKeyword: 'piletid',    hreflang: 'et', priority: 4 },
  // { code: 'lv', locale: 'lv-LV', hubKeyword: 'pasakumi',    ticketKeyword: 'biletes',    hreflang: 'lv', priority: 4 },
  // { code: 'fi', locale: 'fi-FI', hubKeyword: 'tapahtumat',  ticketKeyword: 'liput',      hreflang: 'fi', priority: 4 },
  // { code: 'sv', locale: 'sv-SE', hubKeyword: 'evenemang',   ticketKeyword: 'biljetter',  hreflang: 'sv', priority: 4 },
  // { code: 'da', locale: 'da-DK', hubKeyword: 'begivenheder', ticketKeyword: 'billetter', hreflang: 'da', priority: 4 },
  // { code: 'mt', locale: 'mt-MT', hubKeyword: 'avvenimenti', ticketKeyword: 'biljetti',   hreflang: 'mt', priority: 4 },
];

// --- Derived helpers (used by consumers instead of hardcoding) ---

/** All short language codes: ['pl', 'en', 'de', 'lt', 'fr', ...] */
export const LANGUAGE_CODES = SUPPORTED_LANGUAGES.map(l => l.code);

/**
 * Union type of active language short codes.
 * Must match the `code` values in SUPPORTED_LANGUAGES above.
 * Used as Record key type for city/country slug mappings.
 */
/**
 * Union type for languages that appear in URL prefixes AND in SEO/template
 * data structures. Includes CURRENTLY ACTIVE languages AND temporarily
 * disabled ones — because SEO templates, hub description builders, and
 * slug mappings still reference them (the data stays even when the
 * language is turned off at the routing layer).
 *
 * When re-enabling a language in SUPPORTED_LANGUAGES above, NO change
 * needed here — it's already in the union.
 */
export type ActiveLanguageCode =
  | 'pl' | 'en' | 'de' | 'fr' | 'es'
  | 'lt' | 'it' | 'pt' | 'nl'
  | 'cs' | 'sk' | 'hu' | 'ro' | 'bg' | 'hr' | 'sl' | 'el'
  | 'et' | 'lv' | 'fi' | 'sv' | 'da' | 'mt';

/** All full locale strings: ['pl-PL', 'en-GB', ...] */
export const LANGUAGE_LOCALES: string[] = SUPPORTED_LANGUAGES.map(l => l.locale);

/** Unique hub URL keywords across all languages */
export const HUB_KEYWORDS: string[] = [...new Set(SUPPORTED_LANGUAGES.map(l => l.hubKeyword))];

/** Regex matching any supported language prefix in URL paths: /^\/(pl|en|de|...)/ */
export const LANG_PREFIX_REGEX: RegExp = new RegExp(`^\\/(${LANGUAGE_CODES.join('|')})`);

/** Regex matching language prefix in any position: /\/(pl|en|de|...)\// */
export const LANG_SEGMENT_REGEX: RegExp = new RegExp(`\\/(${LANGUAGE_CODES.join('|')})\\/`);

/** Default language (first in priority order) */
export const DEFAULT_LANGUAGE: LanguageDef = SUPPORTED_LANGUAGES[0];

/** Code→locale lookup: { pl: 'pl-PL', en: 'en-GB', ... } */
export const CODE_TO_LOCALE: Record<string, string> = Object.fromEntries(
  SUPPORTED_LANGUAGES.map(l => [l.code, l.locale])
);

/** Locale→code lookup: { 'pl-PL': 'pl', 'en-GB': 'en', ... } */
export const LOCALE_TO_CODE: Record<string, string> = Object.fromEntries(
  SUPPORTED_LANGUAGES.map(l => [l.locale, l.code])
);

/** Priority map for ISR warmup ordering */
export const LANG_PRIORITY: Record<string, number> = Object.fromEntries(
  SUPPORTED_LANGUAGES.map(l => [l.code, l.priority])
);

/** Structured lang array for sitemap generation: [{ code: 'pl', locale: 'pl-PL' }, ...] */
export const SUPPORTED_LANGS_WITH_LOCALE = SUPPORTED_LANGUAGES.map(l => ({
  code: l.code,
  locale: l.locale,
}));
